import { Component, OnInit, signal, inject, computed, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DialogComponent, ProfileModalContentLibComponent, TitleLibComponent } from '@components/atoms';
import { ShimmerFragmentLibComponent } from '@components/molecules';
import { ProfileScoreService, ProfileModalConfigService } from '@core/services';
import { ItemModalConfigProfileScore } from '@core/interface';
import { ProfileCompletionItem } from '@core/enums';
import { isMobileScreen } from '@core/utils';
import { ErrorAndRetryComponent } from '../error-and-retry/error-and-retry.component';
import { ProfileLevelSectionComponent } from '../profile-level-section/profile-level-section.component';

/**
 * Componente molecule que exibe o card completo de score de completude do perfil.
 *
 * Mostra:
 * - Barra de progresso com percentual
 * - Status de conclusão (ex: "1/4 concluído")
 * - Botão "Detalhes" expansível
 * - Níveis agrupados (Dados pessoais, Contato, Veículo, Carro conectado)
 * - Items individuais com status (completo/pendente)
 *
 * @example
 * <lib-profile-score />
 */
@Component({
  standalone: true,
  selector: 'lib-profile-score',
  imports: [
    TranslateModule,
    DialogComponent,
    ProfileModalContentLibComponent,
    ShimmerFragmentLibComponent,
    TitleLibComponent,
    ErrorAndRetryComponent,
    ProfileLevelSectionComponent
  ],
  templateUrl: './profile-score.component.html',
  styleUrls: ['./profile-score.component.scss'],
})
export class ProfileScoreLibComponent implements OnInit {
  readonly profileScoreService = inject(ProfileScoreService);
  protected modalConfigService = inject(ProfileModalConfigService);

  country = input.required<string>();

  readonly showDetails = signal<boolean>(false);

  // Controle do modal
  readonly showModal = signal<boolean>(false);
  readonly selectedModalConfig = signal<ItemModalConfigProfileScore | null>(null);

  // Enum exposto para uso no template
  readonly ProfileCompletionItem = ProfileCompletionItem;

  // Define todos os items possíveis para cada nível
  readonly levelItems = {
    personalData: [
      ProfileCompletionItem.EMAIL,
      ProfileCompletionItem.NAME,
      ProfileCompletionItem.NATIONAL_ID,
    ],
    contactDetails: [
      ProfileCompletionItem.MOBILE_PHONE,
      ProfileCompletionItem.ADDRESS,
    ],
    vehicle: [
      ProfileCompletionItem.VIN,
    ],
    carConnectedDealer: [
      ProfileCompletionItem.CONNECT_CAR,
      ProfileCompletionItem.PREFERRED_DEALER,
    ],
  };

  // Computed para verificar se o connected_car deve ser exibido
  protected isActiveConnectedCarComputed = computed(() =>
    !this.profileScoreService.scoreProfileComputed()?.carConnectedDealer.notApplicableItems.includes(ProfileCompletionItem.CONNECT_CAR)
  );

  protected isShowProfileScore = computed(() => {
    if (isMobileScreen()) {
      return !this.profileScoreService.scoreProfileComputed()?.completeRegistration;
    }
    return true;
  });

  ngOnInit(): void {
    this.profileScoreService.getCustomerScore(this.country());

  }

  /**
   * Alterna a visualização dos detalhes
   */
  toggleDetails(): void {
    this.showDetails.update((current) => !current);
  }

  /**
   * Handler para quando o link de um item é clicado
   * Abre o modal com as informações configuradas do item
   */
  onLinkClicked(item: ProfileCompletionItem): void {
    const config = this.modalConfigService.getModalConfig(item);
    if (config) {
      this.selectedModalConfig.set(config);
      this.showModal.set(true);
    }
  }

  /**
   * Fecha o modal
   */
  closeModal(): void {
    this.showModal.set(false);
    this.selectedModalConfig.set(null);
  }
}
